import { refreshToken } from './auth';

/*
 * Authenticated request that hands back the Response, status and all.
 *
 * For callers that must tell outcomes apart (a 400 from a 409 from a 403) instead of collapsing every
 * failure into a default the way `fetchWithRetry` does, which is right for optional reads and wrong
 * for writes an admin believes succeeded. Token handling mirrors `fetchWithRetry`: refresh once on
 * 401, then re-issue; a genuine denial is not retried into a loop.
 */
export const authorizedFetch = async (url: URL, method = 'GET', body?: unknown): Promise<Response> => {
  let token = localStorage.getItem('token');
  if (!token) throw new Error('Unauthenticated');

  const headers = new Headers({ Accept: 'application/json', Authorization: `Bearer ${token}` });
  if (body !== undefined) headers.set('Content-Type', 'application/json');
  const init = (): RequestInit => ({
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body)
  });
  let response = await fetch(url, init());

  if (response.status === 401) {
    token = await refreshToken();
    if (!token) throw new Error('Unauthenticated');
    headers.set('Authorization', `Bearer ${token}`);
    response = await fetch(url, init());
  }
  return response;
};

/*
 * The server's own reason for a refusal. The backend answers `{ status, message }` with the reason
 * quoted inside the message (`409 CONFLICT "Reload and try again."`); same extraction as the shared
 * `handleError`, so a message reads the same on every screen. Empty when there is nothing readable.
 */
export const serverMessage = async (response: Response): Promise<string> => {
  try {
    const body = await response.json();
    const raw = typeof body?.message === 'string' ? body.message : '';
    return raw.match(/"([^"]+)"/)?.[1] ?? raw;
  } catch {
    return '';
  }
};
